import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { getFoodGroup } from '../utils/foodGroup';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './MealPlanner.css';

// Returns the Monday of the week containing `date`
function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

// Format a Date as YYYY-MM-DD (local time)
function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Format Mon DD MMM for display
function formatWeekLabel(monday, language) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  const locale = language === 'hu' ? 'hu-HU' : 'en-GB';
  return `${monday.toLocaleDateString(locale, opts)} – ${sunday.toLocaleDateString(locale, opts)}`;
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const DAYS = [0, 1, 2, 3, 4, 5, 6]; // Mon=0 … Sun=6

export default function MealPlanner({ onBack, onSelectRecipe }) {
  const { language } = useLanguage();
  const t = translations[language];

  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [plan, setPlan] = useState([]); // array of meal_plan rows joined with recipe title/image
  const [allRecipes, setAllRecipes] = useState([]); // {id, title, category, image_url}
  const [loading, setLoading] = useState(true);

  // Search modal state
  const [modalSlot, setModalSlot] = useState(null); // { dayOfWeek, mealType } or null
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Shopping list state
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const searchInputRef = useRef(null);

  // Load all recipes once
  useEffect(() => {
    supabase
      .from('recipes')
      .select('id, title, category, image_url')
      .order('title')
      .then(({ data }) => setAllRecipes(data || []));
  }, []);

  // Load meal plan for current week
  const fetchPlan = useCallback(async () => {
    setLoading(true);
    const weekStr = toDateStr(weekStart);
    const { data } = await supabase
      .from('meal_plan')
      .select('id, day_of_week, meal_type, recipe_id, recipes(id, title, image_url, category)')
      .eq('week_start', weekStr);
    setPlan(data || []);
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Focus search input when modal opens
  useEffect(() => {
    if (modalSlot && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [modalSlot]);

  function openModal(dayOfWeek, mealType) {
    setModalSlot({ dayOfWeek, mealType });
    setSearchQuery('');
  }

  function closeModal() {
    setModalSlot(null);
    setSearchQuery('');
  }

  // Get the plan entry for a given slot
  function getSlotEntry(dayOfWeek, mealType) {
    return plan.find(p => p.day_of_week === dayOfWeek && p.meal_type === mealType) || null;
  }

  // Assign a recipe to a slot (upsert)
  async function assignRecipe(recipeId) {
    if (!modalSlot) return;
    setSaving(true);
    const weekStr = toDateStr(weekStart);
    const { dayOfWeek, mealType } = modalSlot;

    // Delete existing entry for this slot if any
    const existing = getSlotEntry(dayOfWeek, mealType);
    if (existing) {
      await supabase.from('meal_plan').delete().eq('id', existing.id);
    }

    // Insert new entry
    await supabase.from('meal_plan').insert({
      week_start: weekStr,
      day_of_week: dayOfWeek,
      meal_type: mealType,
      recipe_id: recipeId,
    });

    await fetchPlan();
    setSaving(false);
    closeModal();
  }

  // Remove a recipe from a slot
  async function removeSlot(dayOfWeek, mealType) {
    const entry = getSlotEntry(dayOfWeek, mealType);
    if (!entry) return;
    await supabase.from('meal_plan').delete().eq('id', entry.id);
    setPlan(prev => prev.filter(p => p.id !== entry.id));
  }

  // Navigate weeks
  function prevWeek() {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
    setGenerated(false);
  }

  function nextWeek() {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
    setGenerated(false);
  }

  // Add all this week's recipes to shopping list
  async function handleGenerateShopping() {
    if (plan.length === 0) return;
    setGenerating(true);

    // Collect unique recipe IDs from the plan
    const recipeIds = [...new Set(plan.map(p => p.recipe_id))];

    // Fetch all ingredients for these recipes
    const { data: allIngredients } = await supabase
      .from('recipe_ingredients')
      .select('recipe_id, ingredient_name, amount, unit')
      .in('recipe_id', recipeIds);

    // Build a title map
    const titleMap = {};
    plan.forEach(p => { titleMap[p.recipe_id] = p.recipes?.title || ''; });

    // Fetch existing unchecked items to merge
    const { data: existing } = await supabase
      .from('shopping_list_items')
      .select('*')
      .eq('checked', false);

    const existingMap = new Map();
    (existing || []).forEach(item => {
      const key = `${item.ingredient_name.toLowerCase().trim()}::${(item.unit || '').toLowerCase()}`;
      existingMap.set(key, item);
    });

    const toInsert = [];
    const toUpdate = [];
    const pendingMap = new Map();

    for (const ing of (allIngredients || [])) {
      const amount = ing.amount != null ? Math.round(ing.amount * 100) / 100 : null;
      const unit = ing.unit || null;
      const key = `${ing.ingredient_name.toLowerCase().trim()}::${(unit || '').toLowerCase()}`;
      const recipeTitle = titleMap[ing.recipe_id] || '';

      if (existingMap.has(key)) {
        const ex = existingMap.get(key);
        const newAmt = (ex.amount != null && amount != null)
          ? Math.round((ex.amount + amount) * 100) / 100
          : (ex.amount ?? amount);
        const existingTitles = ex.recipe_title ? ex.recipe_title.split(', ') : [];
        const titles = existingTitles.includes(recipeTitle)
          ? ex.recipe_title
          : [...existingTitles, recipeTitle].filter(Boolean).join(', ');
        toUpdate.push({ id: ex.id, amount: newAmt, recipe_title: titles });
        existingMap.set(key, { ...ex, amount: newAmt, recipe_title: titles });

      } else if (pendingMap.has(key)) {
        const pending = pendingMap.get(key);
        pending.amount = (pending.amount != null && amount != null)
          ? Math.round((pending.amount + amount) * 100) / 100
          : (pending.amount ?? amount);
        // Append recipe title if not already there
        if (recipeTitle && !pending.recipe_title.split(', ').includes(recipeTitle)) {
          pending.recipe_title = [pending.recipe_title, recipeTitle].filter(Boolean).join(', ');
        }

      } else {
        const item = {
          ingredient_name: ing.ingredient_name,
          amount,
          unit,
          food_group: getFoodGroup(ing.ingredient_name),
          recipe_id: ing.recipe_id,
          recipe_title: recipeTitle,
          checked: false,
        };
        toInsert.push(item);
        pendingMap.set(key, item);
      }
    }

    if (toInsert.length) await supabase.from('shopping_list_items').insert(toInsert);
    for (const u of toUpdate) {
      await supabase.from('shopping_list_items')
        .update({ amount: u.amount, recipe_title: u.recipe_title })
        .eq('id', u.id);
    }

    setGenerating(false);
    setGenerated(true);
    setTimeout(() => setGenerated(false), 4000);
  }

  // Filtered recipe list for the search modal
  const filteredRecipes = searchQuery.trim()
    ? allRecipes.filter(r => r.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : allRecipes;

  const mealTypeLabel = {
    breakfast: t.mealPlanBreakfast,
    lunch: t.mealPlanLunch,
    dinner: t.mealPlanDinner,
  };

  const dayLabels = t.mealPlanDays; // ['Mon','Tue',...]

  // Day dates for column headers (Mon = weekStart, Tue = weekStart+1, ...)
  function dayDate(dayIndex) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dayIndex);
    return d;
  }

  return (
    <div className="meal-planner">
      {/* Header */}
      <div className="meal-planner__header">
        <button className="meal-planner__back" onClick={onBack}>←</button>
        <h2 className="meal-planner__title">{t.mealPlanTitle}</h2>
      </div>

      {/* Week navigation */}
      <div className="meal-planner__week-nav">
        <button className="meal-planner__week-btn" onClick={prevWeek}>‹</button>
        <span className="meal-planner__week-label">
          {formatWeekLabel(weekStart, language)}
        </span>
        <button className="meal-planner__week-btn" onClick={nextWeek}>›</button>
      </div>

      {loading ? (
        <p className="meal-planner__loading">{t.mealPlanLoading}</p>
      ) : (
        <>
          {/* Grid */}
          <div className="meal-planner__grid">
            {/* Day headers */}
            <div className="meal-planner__corner" />
            {DAYS.map(d => {
              const date = dayDate(d);
              const dayNum = date.getDate();
              return (
                <div key={d} className="meal-planner__day-header">
                  <span className="meal-planner__day-name">{dayLabels[d]}</span>
                  <span className="meal-planner__day-num">{dayNum}</span>
                </div>
              );
            })}

            {/* Meal rows */}
            {MEAL_TYPES.map(mealType => (
              <>
                <div key={`label-${mealType}`} className="meal-planner__meal-label">
                  {mealTypeLabel[mealType]}
                </div>
                {DAYS.map(d => {
                  const entry = getSlotEntry(d, mealType);
                  const recipe = entry?.recipes;
                  return (
                    <div
                      key={`${mealType}-${d}`}
                      className={`meal-planner__cell ${recipe ? 'meal-planner__cell--filled' : 'meal-planner__cell--empty'}`}
                    >
                      {recipe ? (
                        <div className="meal-planner__recipe">
                          {recipe.image_url && (
                            <img
                              className="meal-planner__recipe-img"
                              src={recipe.image_url}
                              alt=""
                            />
                          )}
                          <span
                            className="meal-planner__recipe-name"
                            onClick={() => onSelectRecipe && onSelectRecipe(recipe.id)}
                            title={recipe.title}
                          >
                            {recipe.title}
                          </span>
                          <button
                            className="meal-planner__remove-btn"
                            onClick={() => removeSlot(d, mealType)}
                            title={t.mealPlanClearSlot}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <button
                          className="meal-planner__add-btn"
                          onClick={() => openModal(d, mealType)}
                        >
                          {t.mealPlanEmpty}
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            ))}
          </div>

          {/* Generate shopping list */}
          <div className="meal-planner__actions">
            <button
              className="meal-planner__shopping-btn"
              onClick={handleGenerateShopping}
              disabled={generating || plan.length === 0}
            >
              {generating
                ? t.mealPlanGenerating
                : generated
                  ? t.mealPlanAdded
                  : t.mealPlanGenerateShopping}
            </button>
          </div>
        </>
      )}

      {/* Search modal */}
      {modalSlot && (
        <div className="meal-planner__overlay" onClick={closeModal}>
          <div className="meal-planner__modal" onClick={e => e.stopPropagation()}>
            <div className="meal-planner__modal-header">
              <span className="meal-planner__modal-title">
                {dayLabels[modalSlot.dayOfWeek]} · {mealTypeLabel[modalSlot.mealType]}
              </span>
              <button className="meal-planner__modal-close" onClick={closeModal}>×</button>
            </div>
            <input
              ref={searchInputRef}
              className="meal-planner__search-input"
              type="text"
              placeholder={t.mealPlanSearchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div className="meal-planner__results">
              {filteredRecipes.length === 0 ? (
                <p className="meal-planner__no-results">{t.mealPlanNoResults}</p>
              ) : (
                filteredRecipes.map(r => (
                  <button
                    key={r.id}
                    className="meal-planner__result-item"
                    onClick={() => !saving && assignRecipe(r.id)}
                    disabled={saving}
                  >
                    {r.image_url && (
                      <img
                        className="meal-planner__result-img"
                        src={r.image_url}
                        alt=""
                      />
                    )}
                    <span>{r.title}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
